import { type ComponentPropsWithRef, type ElementType, type ReactElement, type ReactNode } from 'react'
import { useTheme } from '@mui/material/styles'

import { getNavigationAndActionIcon } from '@DS/components/icons/navigationAndActionIcons'

import LinkBase from '../_internal/LinkBase'
import { type LinkBaseProps } from '../_internal/LinkBase.types'

import * as styles from './styles'

const ArrowCircleRightIcon = getNavigationAndActionIcon('arrow_circle_right')

export type LinkButtonProps<Component extends ElementType = 'button'> = Omit<
  LinkBaseProps<Component>,
  'underline' | 'locale' | 'isBasePath' | 'isInline' | 'hasNoSpacing' | 'hasDefaultFocusVisible' | 'isObfuscated'
> & {
  component?: Component
  withIcon?: boolean
  isUnderline?: boolean
  isExternal?: boolean
  isObfuscated?: boolean
  children?: ReactNode
  href?: string
} & ComponentPropsWithRef<Component>

const LinkButton = <Component extends ElementType = 'button' | 'a'>({
  withIcon = false,
  isUnderline = false,
  surface = 'onDefault',
  children,
  ...rest
}: LinkButtonProps<Component>): ReactElement | null => {
  const component = rest.component || 'button'
  const theme = useTheme()

  return (
    <LinkBase
      {...rest}
      css={styles.linkButton(theme, surface)}
      component={component}
      underline={isUnderline ? 'always' : 'none'}
      {...(component === 'button' && { type: 'button' })}
      {...(withIcon && { icon: ArrowCircleRightIcon })}
      surface={surface}
    >
      {children}
    </LinkBase>
  )
}

export default LinkButton
